import { isNonQualifyingBoundedVeterinaryLicense } from '../constants/bounded-veterinary-license';
import { registryLicenseKind } from '../constants/license-kind';
import type { OrderEntryFeaturePreference } from '../models/types/order-preferences.types';
import { resolvePrimaryLicense } from '../utils/client-license-primary';
import { isControlledSchedule, isRxSchedule } from '../utils/dea-schedule';
import type {
	OrderCheckFiringLevel,
	OrderCheckLevel,
} from './order-check-level';
import { isOrderCheckOn, isOrderedQuantity } from './order-check-level';
import type { ScheduleLineModel } from './schedule-rules';

/**
 * PD-16276 — the ship-to address's State Board and DEA licenses.
 *
 * An Rx or controlled line needs a valid State Board license at the ship-to
 * address; a controlled line (C2 through C5) also needs a valid DEA license.
 * Each requirement is checked once for the order, and the finding lists the
 * lines that need it so the CRM cart can show it next to each of them.
 */

export type OrderLicenseKind = 'stateBoard' | 'dea';

const LICENSE_LABELS: Readonly<Record<OrderLicenseKind, string>> = {
	stateBoard: 'State Board',
	dea: 'DEA',
};

/**
 * License-type routing. The local client_license.type decides the kind; a
 * matched registry record may word its own type differently, and when it is
 * not a state license it disqualifies the license from the State Board slot
 * (`orderLicenseNonQualifyingReason`).
 *
 * PD-16612: a state license is a state board license or a state drug permit
 * only (license-kind.ts). A label naming a controlled-substance registration
 * ('State Control', pharmsource's label for a state CSR: 45 rows on prod,
 * 2026-09-27) or an animal-euthanasia drug registration is therefore not a
 * State Board label, even though it says STATE. Measured on prod the same day:
 * no ship-to address held such a label as its only State Board license.
 */
export function isStateBoardLicenseType(
	type: string | null | undefined,
): boolean {
	if (type == null) {
		return false;
	}

	const upper = type.toUpperCase();

	if (
		upper.includes('CONTROL') ||
		upper.includes('EUTHANASIA') ||
		upper.includes('PENTOBARBITAL')
	) {
		return false;
	}

	return upper.includes('STATE') || upper.includes('BOARD');
}

export function isDeaLicenseType(type: string | null | undefined): boolean {
	return type != null && type.toUpperCase().includes('DEA');
}

export function isLicenseOfKind(
	type: string | null | undefined,
	kind: OrderLicenseKind,
): boolean {
	return kind === 'dea'
		? isDeaLicenseType(type)
		: isStateBoardLicenseType(type);
}

/**
 * Why a license at the ship-to cannot back an order in the slot its local type
 * files it under:
 *   boundedVeterinary        — PD-16416: a limited, restricted or student
 *                              veterinary license (either slot).
 *   stateControlledSubstance — PD-16612: a State Board license linked to a
 *                              state CSR, which is not a state license.
 *   animalEuthanasia         — PD-16612: a State Board license linked to an
 *                              animal-euthanasia drug registration.
 *   notStateLicense          — PD-16612: a State Board license linked to any
 *                              other registry record that is not a state
 *                              board license or state drug permit.
 */
export type NonQualifyingLicenseReason =
	| 'boundedVeterinary'
	| 'stateControlledSubstance'
	| 'animalEuthanasia'
	| 'notStateLicense';

const NON_QUALIFYING_REASON_TEXT: Readonly<
	Record<NonQualifyingLicenseReason, string>
> = {
	boundedVeterinary: 'a limited, restricted or student veterinary license',
	stateControlledSubstance:
		'a state controlled-substance registration is not a state license',
	animalEuthanasia:
		'an animal euthanasia drug registration is not a state license',
	notStateLicense: 'it is not a state board license or a state drug permit',
};

/** The linked registry record's facts the order check reads. */
export interface OrderLicenseRegistryModel {
	stateCode: string | null | undefined;
	licenseType: string | null | undefined;
	sourceType: string | null | undefined;
	licenseCategory: string | null | undefined;
}

/**
 * The ONE rule deciding whether a license at the ship-to can back an order in
 * its slot, shared by submission validation and the shop endpoint the cart
 * reads. Null = it qualifies (or is judged by the rest of the rules).
 *
 * PD-16612: a license the tenant files as State Board fills the State Board
 * slot only when its linked registry record is a state license (license-kind
 * `registryLicenseKind` 'state'). Before, the local type alone decided, so a
 * state CSR or a shelter's euthanasia registration filed as 'State Board'
 * backed Rx and controlled lines. An unlinked license is unchanged: its local
 * type is all there is to go on.
 */
export function orderLicenseNonQualifyingReason(
	localType: string | null | undefined,
	registry: OrderLicenseRegistryModel | null | undefined,
): NonQualifyingLicenseReason | null {
	if (registry == null) {
		return null;
	}
	if (
		isNonQualifyingBoundedVeterinaryLicense(
			registry.stateCode,
			registry.licenseType,
		)
	) {
		return 'boundedVeterinary';
	}
	if (!isStateBoardLicenseType(localType)) {
		return null;
	}
	const kind = registryLicenseKind({
		sourceType: registry.sourceType,
		licenseCategory: registry.licenseCategory,
		stateCode: registry.stateCode,
		licenseType: registry.licenseType,
	});
	if (kind === 'state') {
		return null;
	}
	if (kind === 'stateControlledSubstance') {
		return 'stateControlledSubstance';
	}
	if (kind === 'animalEuthanasia') {
		return 'animalEuthanasia';
	}
	return 'notStateLicense';
}

export interface OrderLicenseCandidateModel {
	type: string;
	externalId?: string | null;
	isPrimary?: boolean | null;
	/**
	 * PD-16416 — linked to a registry record that can never back an order in
	 * this license's slot. See `orderLicenseNonQualifyingReason`.
	 */
	isNonQualifying?: boolean | null;
	/** PD-16612 — why; absent means the PD-16416 bounded veterinary reason. */
	nonQualifyingReason?: NonQualifyingLicenseReason | null;
	/**
	 * PD-17159 — in force by `isClientLicenseInForce` (the linked registry
	 * status, else the license's own, through the can-order table). When the
	 * address holds both an in-force and a not-in-force license of the kind,
	 * the in-force one is checked, even over a flagged primary.
	 */
	inForce?: boolean | null;
}

/**
 * The license of a kind that order validation acts on at one address: the
 * primary one (PD-14845), or the first candidate when no primary can be
 * resolved. PD-17159: an in-force license is chosen over a not-in-force one
 * first (see resolvePrimaryLicense step 0).
 *
 * PD-16416: a non-qualifying license is passed over when the address holds a
 * qualifying one of the same kind, so a bounded veterinary license flagged
 * primary cannot shadow the license that actually backs the order. When it is
 * the only one of its kind it is still returned, so the finding names the
 * license the rep can see on file rather than claiming none is on file.
 */
export function selectLicenseForOrderCheck<
	T extends OrderLicenseCandidateModel,
>(licenses: ReadonlyArray<T>, kind: OrderLicenseKind): T | undefined {
	const ofKind = licenses.filter((license) =>
		isLicenseOfKind(license.type, kind),
	);
	const qualifying = ofKind.filter(
		(license) => license.isNonQualifying !== true,
	);
	const candidates = qualifying.length > 0 ? qualifying : ofKind;
	const primary = resolvePrimaryLicense(
		candidates.map((license) => ({
			license,
			externalId: license.externalId ?? null,
			isPrimary: license.isPrimary ?? null,
			inForce: license.inForce ?? null,
		})),
	);

	// PD-17159: when no primary resolves, fall back to an in-force license
	// before the first one, so the same in-force-first rule holds when the
	// in-force rows are themselves ambiguous. Unchanged when none, or all, are
	// in force (the first in-force row is then the first row).
	return (
		primary?.license ??
		candidates.find((license) => license.inForce === true) ??
		candidates.at(0)
	);
}

/**
 * PD-16823 — a DEA registration covers the one location it is issued for (21
 * CFR 1301.12(a): one registration per location where controlled substances
 * are dispensed or kept), so a DEA registered in another state than the order ships to does
 * not cover the order. The same test the Compliance list applies to an
 * account's locations (compliance-sql.ts deaRegisteredStateSql /
 * deaRegisteredAtClientLocationSql), here against the order's ship-to.
 */
export interface DeaRegisteredOutOfStateModel {
	/** The state the DEA registration is registered in. */
	registeredState: string;
	/** The state of the order's ship-to address. */
	shipToState: string;
}

/** The linked registry row's facts the DEA state comparison reads. */
export interface DeaRegistryStateModel {
	sourceType: string | null | undefined;
	/** The registered address state (lm_registry.state). */
	state?: string | null | undefined;
	/** lm_registry.state_code, read when the address state is blank. */
	stateCode: string | null | undefined;
}

function normalizedState(value: string | null | undefined): string | null {
	const trimmed = (value ?? '').trim().toUpperCase();
	return trimmed === '' ? null : trimmed;
}

/**
 * The registered state of a license linked to a DEA registry row against the
 * ship-to state, or null when it covers the order. Null too when the license
 * is not linked to a DEA registry row (an unlinked DEA keeps today's checks),
 * or when either state is blank: a blank state does not disprove coverage.
 */
export function deaRegisteredOutOfState(
	registry: DeaRegistryStateModel | null | undefined,
	shipToState: string | null | undefined,
): DeaRegisteredOutOfStateModel | null {
	if (
		registry == null ||
		(registry.sourceType ?? '').trim().toUpperCase() !== 'DEA'
	) {
		return null;
	}

	const registeredState =
		normalizedState(registry.state) ?? normalizedState(registry.stateCode);
	const shipTo = normalizedState(shipToState);

	if (registeredState == null || shipTo == null || registeredState === shipTo) {
		return null;
	}

	return { registeredState, shipToState: shipTo };
}

export interface OrderLicenseValidityModel {
	licenseNumber: string | null | undefined;
	/** True when the license's validation expiration date is in the past. */
	isExpired: boolean;
}

/** A license can back an order when it has a number and has not expired. */
export function isLicenseValidForOrder(
	license: OrderLicenseValidityModel | null | undefined,
): boolean {
	return (
		license != null &&
		license.licenseNumber != null &&
		license.licenseNumber.trim() !== '' &&
		!license.isExpired
	);
}

/**
 * Words a failure by its cause (PD-14937). A license missing from the ship-to
 * address while the client holds one on another address is an address
 * mismatch, not an expired license.
 */
export function buildLicenseFailureMessage(
	kind: OrderLicenseKind,
	licenseOnFile: boolean,
	existsElsewhere: boolean,
): string {
	const label = LICENSE_LABELS[kind];

	if (!licenseOnFile) {
		return existsElsewhere
			? `Shipping address does not match the ${label} license address`
			: `No ${label} license on file for the shipping address`;
	}

	return `${label} license invalid or expired`;
}

/** Registry standing, read only when the tenant has pharmacy compliance on. */
export interface LicenseStandingModel {
	/** Whether the status permits ordering (Active or Probation, PD-14745). */
	canOrder: boolean;
	status: string | null;
}

export interface OrderLicenseSubjectModel extends OrderLicenseValidityModel {
	standing?: LicenseStandingModel | null;
	/** PD-16416 — see `OrderLicenseCandidateModel.isNonQualifying`. */
	isNonQualifying?: boolean | null;
	/** PD-16612 — see `OrderLicenseCandidateModel.nonQualifyingReason`. */
	nonQualifyingReason?: NonQualifyingLicenseReason | null;
	/**
	 * PD-16823 — a DEA linked to a DEA registry row registered in another state
	 * than the order ships to (`deaRegisteredOutOfState`). Read for the DEA
	 * slot only.
	 */
	registeredOutOfState?: DeaRegisteredOutOfStateModel | null;
}

export interface LicenseRequirementModel {
	level: OrderCheckLevel | null | undefined;
	/** The license selected at the ship-to address, or null when there is none. */
	license: OrderLicenseSubjectModel | null | undefined;
	/** Whether the client holds this kind of license on another address. */
	existsElsewhere: boolean;
}

export interface LicenseRequirementsInputModel {
	lines: ReadonlyArray<ScheduleLineModel>;
	stateBoard: LicenseRequirementModel;
	dea: LicenseRequirementModel;
}

export type LicenseFindingReason =
	| 'missing'
	| 'addressMismatch'
	| 'invalid'
	| 'notInGoodStanding'
	| 'notQualifying'
	| 'registeredOutOfState';

export interface LicenseFindingModel {
	kind: OrderLicenseKind;
	reason: LicenseFindingReason;
	level: OrderCheckFiringLevel;
	message: string;
	/** The lines that need this license. */
	itemIds: Array<number>;
}

export function checkLicenseRequirements(
	input: LicenseRequirementsInputModel,
): Array<LicenseFindingModel> {
	const orderedLines = input.lines.filter((line) =>
		isOrderedQuantity(line.quantity),
	);
	const stateBoardItemIds = orderedLines
		.filter(
			(line) =>
				isRxSchedule(line.schedule) || isControlledSchedule(line.schedule),
		)
		.map((line) => line.itemId);
	const deaItemIds = orderedLines
		.filter((line) => isControlledSchedule(line.schedule))
		.map((line) => line.itemId);

	return [
		...checkLicenseRequirement(
			'stateBoard',
			input.stateBoard,
			stateBoardItemIds,
		),
		...checkLicenseRequirement('dea', input.dea, deaItemIds),
	];
}

function checkLicenseRequirement(
	kind: OrderLicenseKind,
	requirement: LicenseRequirementModel,
	itemIds: Array<number>,
): Array<LicenseFindingModel> {
	const level = requirement.level;

	if (itemIds.length === 0 || !isOrderCheckOn(level)) {
		return [];
	}

	const label = LICENSE_LABELS[kind];
	const license = requirement.license ?? null;
	const findings: Array<LicenseFindingModel> = [];

	// PD-16416 / PD-16612: a license that cannot back an order in this slot (a
	// bounded veterinary license, or a State Board license that is not a state
	// license) never does, whatever its number, expiration or standing say, so
	// it is the one finding reported.
	if (license?.isNonQualifying === true) {
		const why =
			NON_QUALIFYING_REASON_TEXT[
				license.nonQualifyingReason ?? 'boundedVeterinary'
			];
		return [
			{
				kind,
				reason: 'notQualifying',
				level,
				message: `${label} license does not qualify: ${why}`,
				itemIds,
			},
		];
	}

	if (!isLicenseValidForOrder(license)) {
		let reason: LicenseFindingReason = 'invalid';

		if (license == null) {
			reason = requirement.existsElsewhere ? 'addressMismatch' : 'missing';
		}

		findings.push({
			kind,
			reason,
			level,
			message: buildLicenseFailureMessage(
				kind,
				license != null,
				requirement.existsElsewhere,
			),
			itemIds,
		});
	}

	// PD-16823: a DEA registered in another state does not cover an order
	// shipping here, however current it is. Same level as the DEA check.
	const outOfState = kind === 'dea' ? license?.registeredOutOfState : null;

	if (outOfState != null) {
		const number = license?.licenseNumber?.trim() ?? '';
		findings.push({
			kind,
			reason: 'registeredOutOfState',
			level,
			message: `${number === '' ? label : `${label} ${number}`} is registered in ${outOfState.registeredState}; this order ships to ${outOfState.shipToState}.`,
			itemIds,
		});
	}

	const standing = license?.standing;

	// Order validation cares only whether the status permits ordering
	// (PD-14745); Probation still can. A board's disciplinary action is not a
	// checkout finding: Compliance dispositions it on the dashboard (PD-16669).
	if (standing != null && !standing.canOrder) {
		findings.push({
			kind,
			reason: 'notInGoodStanding',
			level,
			message: `${label} license is not in orderable standing (${standing.status ?? 'Unknown'})`,
			itemIds,
		});
	}

	return findings;
}

/**
 * PD-16333 — Vendor Chargeback Items.
 *
 * A vendor chargeback item is one with a manufacturer-contracted price: the
 * distributor sells it under a contract and claims the difference back from
 * the manufacturer, and the manufacturer pays that chargeback only for a
 * customer with a valid DEA registration. Most of these items carry no DEA
 * schedule, so the DEA License check (controlled lines only) never looks at
 * them. This check asks for the same DEA license on any line whose item is
 * flagged, by the very same rule (`checkLicenseRequirement`), at its own
 * per-channel level.
 *
 * The flag is an item custom field the tenant's ERP import writes
 * (Pharmsource: an active row in NetSuite's customrecord_contracted_price).
 */
export const VENDOR_CHARGEBACK_ITEM_FIELD_KEY = 'has_contracted_price';

/**
 * Whether an item's custom fields mark it as a vendor chargeback item. The
 * value arrives as a JSON boolean from the item column, but as the text
 * 'true' / 'false' through a `cf:` list or batch field (JSON_UNQUOTE), and a
 * hand-edited value may be 1 or '1'. Anything else, including a missing key,
 * is not flagged. A JSON string of the whole object is accepted too, because
 * some reads hand the column back unparsed.
 */
export function hasContractedPrice(customFields: unknown): boolean {
	let fields: unknown = customFields;

	if (typeof fields === 'string') {
		try {
			fields = JSON.parse(fields) as unknown;
		} catch {
			return false;
		}
	}

	if (fields == null || typeof fields !== 'object' || Array.isArray(fields)) {
		return false;
	}

	// Own keys only: a flag inherited through the prototype is not the item's.
	if (
		!Object.prototype.hasOwnProperty.call(
			fields,
			VENDOR_CHARGEBACK_ITEM_FIELD_KEY,
		)
	) {
		return false;
	}

	return isContractedPriceValue(
		(fields as Record<string, unknown>)[VENDOR_CHARGEBACK_ITEM_FIELD_KEY],
	);
}

/** The one value test, for a single `cf:has_contracted_price` field. */
export function isContractedPriceValue(value: unknown): boolean {
	return value === true || value === 1 || value === 'true' || value === '1';
}

/**
 * The tenant switch (`instance-feature-order-entry` →
 * `vendorChargebackItems.isEnabled`). A missing preference or key is off:
 * unlike the license checks, which treat a missing preference as an error,
 * this check exists only for a tenant that asks for it.
 */
export function isVendorChargebackItemsEnabled(
	preference: OrderEntryFeaturePreference | null | undefined,
): boolean {
	return preference?.vendorChargebackItems?.isEnabled === true;
}

export interface VendorChargebackLineModel {
	itemId: number;
	quantity: number;
	/** The item is a vendor chargeback item (`hasContractedPrice`). */
	hasContractedPrice: boolean;
}

export interface VendorChargebackDeaInputModel {
	lines: ReadonlyArray<VendorChargebackLineModel>;
	/**
	 * The Vendor Chargeback Items check's own level, with the DEA license the
	 * DEA License check selects at the ship-to and whether the client holds a
	 * DEA on another address.
	 */
	dea: LicenseRequirementModel;
}

/** Prefixes every finding so it is never mistaken for the DEA License check's. */
export const VENDOR_CHARGEBACK_MESSAGE_PREFIX = 'Vendor chargeback item: ';

/**
 * PD-16333: what a storefront customer sees under a contracted line that needs
 * a DEA license. "Vendor chargeback" is Pharmsource's internal term, so the
 * storefront says only what the customer has to fix. The CRM keeps the
 * prefixed message, which tells staff why the line is held.
 */
export const VENDOR_CHARGEBACK_STOREFRONT_MESSAGE =
	'A valid DEA license is required to purchase this item.';

/** One contracted line that needs a DEA license, for a per-line message. */
export interface VendorChargebackItemFindingModel {
	itemId: number;
	level: OrderCheckFiringLevel;
	reason: LicenseFindingReason;
}

/**
 * PD-16333: splits the vendor chargeback findings into one entry per line, so
 * the storefront can show its message under each line rather than in the
 * cart-wide alert. A line that several findings name is listed once, with the
 * first finding's reason.
 */
export function groupVendorChargebackFindingsByItem(
	findings: ReadonlyArray<LicenseFindingModel>,
): Array<VendorChargebackItemFindingModel> {
	const byItem = new Map<number, VendorChargebackItemFindingModel>();

	for (const finding of findings) {
		for (const itemId of finding.itemIds) {
			if (!byItem.has(itemId)) {
				byItem.set(itemId, {
					itemId,
					level: finding.level,
					reason: finding.reason,
				});
			}
		}
	}

	return [...byItem.values()];
}

/**
 * The DEA findings for the vendor chargeback lines of an order: missing,
 * address mismatch, invalid or expired, not qualifying, registered in another
 * state, and (when the subject carries registry standing) a status that cannot
 * order. One finding per reason, listing the lines that need the license, as
 * the DEA License check reports them.
 */
export function checkVendorChargebackDea(
	input: VendorChargebackDeaInputModel,
): Array<LicenseFindingModel> {
	const itemIds = input.lines
		.filter(
			(line) => line.hasContractedPrice && isOrderedQuantity(line.quantity),
		)
		.map((line) => line.itemId);

	return checkLicenseRequirement('dea', input.dea, itemIds).map(
		(finding) => ({
			...finding,
			message: `${VENDOR_CHARGEBACK_MESSAGE_PREFIX}${finding.message}`,
		}),
	);
}
